import {computed, ComputedRef} from 'vue';
import {MobileContextMenu} from '@/components/types';
import {ScreenCoordinates} from '@/types/types';

// TODO: this file is named as if it's a workaround for statically accessing vue class components, but I don't know which class would actually be the one being accessed.

const TOUCH_TIMEOUT_CONTEXT_MENU: number = 400;
const TOUCH_MOVE_THRESHOLD_CONTEXT_MENU: number = 15;

let contextMenuTouchStartClient: ScreenCoordinates | null = null;
let contextMenuTouchStartScreen: ScreenCoordinates | null = null;
let contextMenuTouchCurrent: ScreenCoordinates | null = null;
let contextMenuTouchTimeout: number | null = null;
let contextMenuShouldOpen: boolean = false;
let contextMenuPromiseResolve: () => void = (): void => {
    return;
};
let contextMenuPromiseReject: () => void = (): void => {
    return;
};

const isIOS: ComputedRef<boolean> = computed((): boolean => {
    return (/iPad|iPhone|iPod/.test(navigator.platform)
        || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1))
    && !window.MSStream;
});
const isAndroid: ComputedRef<boolean> = computed((): boolean => {
    return (/Android/i).test(navigator.userAgent);
});
const isTargetedDevice: ComputedRef<boolean> = computed((): boolean => {
    return isIOS.value || isAndroid.value;
});

function cancelContextMenu(): void {
    if (contextMenuTouchTimeout !== null) {
        clearTimeout(contextMenuTouchTimeout);
    }
    contextMenuTouchStartClient = null;
    contextMenuTouchStartScreen = null;
    contextMenuTouchCurrent = null;
    contextMenuTouchTimeout = null;
    contextMenuShouldOpen = false;
    contextMenuPromiseResolve = (): void => {
        return;
    };
    contextMenuPromiseReject = (): void => {
        return;
    };
}

/**
 * @param event Should only be touchstart
 */
function startTouch(event: TouchEvent): Promise<ScreenCoordinates> | null {
    if (!isTargetedDevice.value) {
        return null;
    }

    if (event.touches.length > 1) {
        // multiple touches => not context menu
        cancelContextMenu();

        return null;
    }

    contextMenuTouchStartClient = {
        x: event.changedTouches[0].clientX,
        y: event.changedTouches[0].clientY,
    };
    contextMenuTouchStartScreen = {
        x: event.changedTouches[0].screenX,
        y: event.changedTouches[0].screenY,
    };
    contextMenuTouchTimeout = setTimeout((): void => {
        contextMenuShouldOpen = true;
    }, TOUCH_TIMEOUT_CONTEXT_MENU);

    return new Promise<ScreenCoordinates>((resolve: (value: PromiseLike<ScreenCoordinates> | ScreenCoordinates) => void, reject: () => void): void => {
        contextMenuPromiseResolve = (): void => {
            resolve(contextMenuTouchStartClient!);
        };
        contextMenuPromiseReject = (): void => {
            reject();
            cancelContextMenu();
        };
    });
}

/**
 * @param event Should only be touchmove|dragover|dragleave
 */
function moveTouch(event: TouchEvent | DragEvent): void {
    if (!isTargetedDevice.value || contextMenuTouchStartScreen === null) {
        return;
    }

    if (!isTouchEvent(event)) {
        registerPositionOnScreen(event);
    }

    if (detectMovement(event)) {
        contextMenuPromiseReject();
    }
}

/**
 * @param event Should only be touchend | dragend
 */
function endTouch(event: TouchEvent | DragEvent): void {
    if (!isTargetedDevice.value || contextMenuTouchStartScreen === null) {
        return;
    }

    if (contextMenuShouldOpen && !detectMovement(event) && event.type !== 'contextmenu') {
        contextMenuPromiseResolve();
    }
    cancelContextMenu();
}

/**
 * Should be called on dragstart events
 * @param event
 */
function registerPositionOnScreen(event: DragEvent): void {
    contextMenuTouchCurrent = {
        x: event.screenX,
        y: event.screenY,
    };
}

function detectMovement(event: TouchEvent | DragEvent): boolean {
    if (contextMenuTouchStartScreen === null) {
        return true;
    }

    let currentX: number;
    let currentY: number;
    if (isTouchEvent(event)) {
        currentX = event.changedTouches[0].screenX;
        currentY = event.changedTouches[0].screenY;
    } else if (event.type === 'dragend') {
        currentX = contextMenuTouchCurrent!.x;
        currentY = contextMenuTouchCurrent!.y;
    } else {
        currentX = event.screenX;
        currentY = event.screenY;
    }

    const distance: number = Math.sqrt(Math.pow(contextMenuTouchStartScreen.x - currentX, 2) + Math.pow(contextMenuTouchStartScreen.y - currentY, 2));

    return (distance > TOUCH_MOVE_THRESHOLD_CONTEXT_MENU);
}

function isTouchEvent(event: TouchEvent | DragEvent): event is TouchEvent {
    return !!(event as TouchEvent).changedTouches;
}

export default function useMobileContextMenu(): MobileContextMenu {
    return {
        startTouch,
        moveTouch,
        endTouch,
        registerPositionOnScreen,
        isTargetedDevice,
        isIOS,
        isAndroid,
    };
}
